import type { MsgKey } from "./i18n";
import { PUZZLE_GRID_OPTIONS } from "./puzzle.ts";
import type { RewardItem, RewardKind } from "./reward.ts";

/**
 * 关卡多图（改版，host 2026-10-21 [721][734][741]）的前端模型。服务端：/admin/api/config/versions/:v/stages 与 /stage-images。
 *
 * - 关卡 = 每位 idol 下的一个编号（1、2、3…），与拼图列表（cfg_puzzle）**无关**；服务端分配稳定 ID `st_NNNN`，编号只是可改的显示字段；
 * - 图挂在关卡下，第 1 张就是 `slot = 1`、`imageType = "stage"` 的一行（关卡图），不再合成；
 * - 规格 4～6 **必填**，没有「跟随」；碎片价只有碎片拼图能 > 0（草稿可 0，发布要 ≥ 1）；
 * - 关卡奖励 `[{kind, ref?, qty}]`，种类只开骰子 / 金币 / 拼图碎片（host [741]），金币按填的数量原样发、不乘 B。
 *
 * ID 套路照关卡相册（lib/idol-album.ts）：`null` = 本地新建、还没落库；`localKey` 是建行时的 UUID，随 POST 作为 uid 落库。
 * **图片的 `stageId` 在本地新建关卡时先填那一关的 localKey**：关卡落库拿到 `st_` 号后，保存流程按回执换成真 ID 再建图（见 App 的保存段）。
 */

/** 关卡奖励可选的种类（与服务端发布闸白名单一致） */
export const STAGE_REWARD_KINDS: RewardKind[] = ["spin", "money", "puzzle", "gems", "free", "wildcard"];
/** 关卡解锁花费可选的种类（与服务端 STAGE_UNLOCK_COST_KINDS 一致），每种至多一项 */
export const STAGE_UNLOCK_KINDS: RewardKind[] = ["money", "puzzle"];
/** 关卡第 2 张起可选的类型。第 1 张恒为 stage，不能选也不能改 */
export const STAGE_IMAGE_TYPES = ["free", "frag", "vip"] as const;
export const IMAGE_TYPE_STAGE = "stage";
export const IMAGE_TYPE_FRAG = "frag";
/** 一关最多几张（含第 1 张、含停用行）。与服务端发布闸一致 */
export const STAGE_IMAGE_MAX = 12;
/** 新建图片的默认规格（必填，运营可改） */
export const DEFAULT_GRID_N = 4;
/** 与服务端 INT32_MAX 一致 */
const INT_MAX = 2147483647;

export interface Stage {
  /** 服务端分配的 `st_NNNN`；null = 本地新建、尚未提交 */
  stageId: string | null;
  localKey: string;
  idolId: string;
  /** 自然数编号 1、2、3…。同一 idol 下唯一只由发布闸拦（改名对调时中间态必然重复） */
  levelCode: string;
  reward: RewardItem[];
  /** 解锁花费（金币 / 拼图碎片，每种至多一项）；空 = 不花。上一关拼完之外叠加的条件，全部满足才开放 */
  unlockCost: RewardItem[];
  /** 需要 VIP。V1 没有 VIP 入口：勾了就对所有玩家锁着 */
  unlockVip: boolean;
  enabled: boolean;
}

export interface StageImage {
  imageId: string | null;
  localKey: string;
  /** 所属关卡：落库的关是 `st_` 号；本地新建、还没落库的关先填它的 localKey */
  stageId: string;
  /** 第几张，≥ 1；第 1 张是关卡图 */
  slot: number;
  /** stage | free | frag | vip。库里约定外的值原样保留（界面标红） */
  imageType: string;
  gridN: number;
  priceFrag: number;
  /** 第一次拼完给这一关 idol 的亲密度点数；0 = 不给 */
  intimacyPoints: number;
  thumbImg: string;
  img: string;
  /** 标签（只给运营归类，C 端不下发）。候选项见 stageImageTagChoices */
  tags: string[];
  enabled: boolean;
}

/** 服务端的解锁花费：`[{kind: money | puzzle, qty}]`，money 的 qty 是十进制字符串 */
export interface ServerStage extends Omit<Stage, "stageId" | "localKey" | "unlockCost" | "unlockVip"> {
  stageId: string; uid?: string; imageCount?: number; unlockCost?: Array<{ kind: string; qty: number | string | null }>; unlockVip?: boolean;
}
/** intimacyPoints 是 2026-10-23 加的，老回包没有（按 0）；tags 是 2026-10-30 加的（按 []） */
export interface ServerStageImage extends Omit<StageImage, "imageId" | "localKey" | "intimacyPoints" | "tags"> {
  imageId: string; uid?: string; intimacyPoints?: number; tags?: string[];
}
export type StageBody = Omit<ServerStage, "stageId" | "imageCount">;
export type StageImageBody = Omit<ServerStageImage, "imageId">;

const num = (v: unknown, dflt = 0) => (v == null || v === "" || !Number.isFinite(Number(v)) ? dflt : Number(v));
const uuid = () => globalThis.crypto.randomUUID();

export const toStage = (r: ServerStage): Stage => ({
  stageId: r.stageId, localKey: r.uid || r.stageId, idolId: r.idolId ?? "", levelCode: r.levelCode ?? "",
  reward: Array.isArray(r.reward) ? r.reward.map((x) => ({ kind: x.kind, ...(x.ref ? { ref: x.ref } : {}), qty: x.qty == null ? null : num(x.qty) })) : [],
  unlockCost: (r.unlockCost ?? []).map((x) => ({ kind: x.kind as RewardKind, qty: x.qty == null ? null : num(x.qty) })),
  unlockVip: !!r.unlockVip,
  enabled: !!r.enabled,
});
export const toStageImage = (r: ServerStageImage): StageImage => ({
  imageId: r.imageId, localKey: r.uid || r.imageId, stageId: r.stageId ?? "", slot: num(r.slot), imageType: r.imageType ?? "",
  gridN: num(r.gridN), priceFrag: num(r.priceFrag), intimacyPoints: num(r.intimacyPoints), thumbImg: r.thumbImg ?? "", img: r.img ?? "",
  tags: Array.isArray(r.tags) ? r.tags : [], enabled: !!r.enabled,
});
export const toServerStageBody = (s: Stage): StageBody => ({
  uid: s.localKey, idolId: s.idolId, levelCode: s.levelCode,
  // 数量框清空了的奖励行先不发（理由同下面的解锁花费）
  reward: s.reward.filter((x) => x.qty != null).map((x) => ({ kind: x.kind, ...(x.ref ? { ref: x.ref } : {}), qty: x.qty })),
  // 服务端金币收十进制字符串（与奖励同口径）。没填数量 / 重复种类的行先不发（存不下，界面上标红让人改）——
  // 带上它整笔 400，自动保存会卡在错误态
  unlockCost: s.unlockCost
    .filter((x, i, all) => x.qty != null && all.findIndex((y) => y.kind === x.kind) === i)
    .map((x) => ({ kind: x.kind, qty: x.kind === "money" ? String(x.qty) : x.qty })),
  unlockVip: s.unlockVip, enabled: s.enabled,
});
/** `stageId` 由调用方传入真 ID（本地新建的关已在保存流程里换成 `st_` 号） */
export const toServerStageImageBody = (x: StageImage, stageId: string): StageImageBody => ({
  uid: x.localKey, stageId, slot: x.slot, imageType: x.imageType, gridN: x.gridN, priceFrag: x.priceFrag, intimacyPoints: x.intimacyPoints,
  thumbImg: x.thumbImg, img: x.img, tags: x.tags, enabled: x.enabled,
});

/**
 * 关卡图片标签的下拉候选：本版本所有关卡图片用过的标签，去重后按字典序。
 * 没有单独的标签字典——手填一个新标签、这张图存下之后，它就出现在下一次新增 / 编辑的下拉里；
 * 反过来，最后一张用它的图删了或去掉了它，它也就从候选里消失
 */
export const stageImageTagChoices = (rows: readonly StageImage[]): string[] =>
  [...new Set(rows.flatMap((x) => x.tags))].sort((a, b) => a.localeCompare(b));

export const isNewStage = (s: Stage) => s.stageId == null;
export const isNewStageImage = (x: StageImage) => x.imageId == null;
/** 新建图片时填的所属关：落库的按 st_ 号、本地新建的按 localKey */
export const stageRef = (s: Stage) => s.stageId ?? s.localKey;
/**
 * 这张图属不属于这一关。**两种写法都认**：本地新建的关存盘拿到 st_ 号后（回执套回），
 * 它名下本地新建的图在下一次读回之前仍写着 localKey——只认 st_ 号的话这些图会从页面上「消失」。
 * localKey 是 UUID，不可能与 st_ 号撞
 */
export const belongsTo = (x: StageImage, s: Stage) => x.stageId === s.localKey || (s.stageId != null && x.stageId === s.stageId);

export const newStage = (idolId: string, levelCode: string): Stage => ({
  stageId: null, localKey: uuid(), idolId, levelCode, reward: [], unlockCost: [], unlockVip: false, enabled: true,
});

const newImage = (stageKey: string, slot: number, imageType: string, gridN = DEFAULT_GRID_N): StageImage => ({
  imageId: null, localKey: uuid(), stageId: stageKey, slot, imageType, gridN,
  priceFrag: 0, intimacyPoints: 0, thumbImg: "", img: "", tags: [], enabled: true,
});
/** 新建关卡带出的默认 6 张（host [625]）：关卡图 + 免费 ×3 + 碎片拼图 + VIP 拼图，图都留空由运营传 */
export const templateImages = (stageKey: string): StageImage[] =>
  ([IMAGE_TYPE_STAGE, "free", "free", "free", IMAGE_TYPE_FRAG, "vip"] as const).map((ty, i) => newImage(stageKey, i + 1, ty));
/** 末尾加一张：默认免费、默认规格 */
export const appendImage = (s: Stage, rows: StageImage[]): StageImage =>
  newImage(stageRef(s), Math.max(0, ...rows.filter((x) => belongsTo(x, s)).map((x) => x.slot)) + 1, "free");

// ─────────────────────────────────────────────────────────────────────────────
// 编号
// ─────────────────────────────────────────────────────────────────────────────

/** 关卡编号：自然数 1、2、3…（2026-10-22 起，与服务端 game/stage-config.ts STAGE_CODE_RE 一致；老拼图的「使用关卡编号」仍是 1-1，另一回事） */
export const STAGE_CODE_RE = /^[1-9][0-9]{0,4}$/;
/** 编号排序键：数值序；格式不对的排最后 */
export const levelCodeKey = (c: string): number => (STAGE_CODE_RE.test(c) ? Number(c) : Infinity);
export const byLevelCode = (a: { levelCode: string }, b: { levelCode: string }) => {
  const x = levelCodeKey(a.levelCode), y = levelCodeKey(b.levelCode);
  return (x === y ? 0 : x < y ? -1 : 1) || a.levelCode.localeCompare(b.levelCode);
};
/** 同一 idol 下编号重复的关（localKey）。只提示、不进保存闸（与服务端：写接口不判，发布闸拦） */
export function duplicateLevelCodes(stages: Stage[]): Set<string> {
  const seen = new Map<string, string[]>();
  for (const s of stages) {
    const k = `${s.idolId}\u0000${s.levelCode}`;
    seen.set(k, [...(seen.get(k) ?? []), s.localKey]);
  }
  return new Set([...seen.values()].filter((g) => g.length > 1).flat());
}
/** 新建关卡时给的默认编号：这位 idol 现有最大编号 + 1；一关都没有给 1 */
export function nextLevelCode(stages: Stage[], idolId: string): string {
  const nums = stages.filter((s) => s.idolId === idolId && STAGE_CODE_RE.test(s.levelCode)).map((s) => Number(s.levelCode));
  return String(nums.length ? Math.max(...nums) + 1 : 1);
}

// ─────────────────────────────────────────────────────────────────────────────
// 问题清单（保存闸 = 写接口会 400 的；发布提示 = 草稿存得下、发布被拦的）
// ─────────────────────────────────────────────────────────────────────────────

const rewardFault = (items: RewardItem[]): MsgKey | null => {
  for (const x of items) {
    if (!STAGE_REWARD_KINDS.includes(x.kind)) return "stage.iss.rewardKind";
    if (x.qty == null || !Number.isInteger(x.qty) || x.qty < 1) return "stage.iss.rewardQty";
  }
  return null;
};

/** 关卡存不下去的问题 */
export function stageIssues(s: Stage): MsgKey[] {
  const out: MsgKey[] = [];
  if (!s.idolId) out.push("stage.iss.noIdol");
  if (!STAGE_CODE_RE.test(s.levelCode)) out.push("stage.iss.badCode");
  const rf = rewardFault(s.reward);
  if (rf) out.push(rf);
  if (s.unlockCost.some((x) => !STAGE_UNLOCK_KINDS.includes(x.kind)) || new Set(s.unlockCost.map((x) => x.kind)).size !== s.unlockCost.length) {
    out.push("stage.iss.unlockKind");
  } else if (s.unlockCost.some((x) => x.qty == null || !Number.isSafeInteger(x.qty) || x.qty < 1)) out.push("stage.iss.unlockQty");
  return out;
}
export const stageBlocker = (s: Stage): MsgKey | null => stageIssues(s)[0] ?? null;

export const isKnownImageType = (v: string) => v === IMAGE_TYPE_STAGE || (STAGE_IMAGE_TYPES as readonly string[]).includes(v);
const gridOk = (n: number) => (PUZZLE_GRID_OPTIONS as readonly number[]).includes(n);

/** 图片存不下去的问题（与服务端写接口同口径） */
export function stageImageIssues(x: StageImage): MsgKey[] {
  const out: MsgKey[] = [];
  if (!Number.isInteger(x.slot) || x.slot < 1 || x.slot > INT_MAX) out.push("stage.iss.badSlot");
  if (!isKnownImageType(x.imageType)) out.push("album.iss.badImageType");
  if (!gridOk(x.gridN)) out.push("stage.iss.badGridN");
  if (!Number.isInteger(x.priceFrag) || x.priceFrag < 0 || x.priceFrag > INT_MAX) out.push("album.iss.badPriceFrag");
  else if (x.imageType !== IMAGE_TYPE_FRAG && x.priceFrag !== 0) out.push("album.iss.priceOnlyFrag");
  return out;
}
/** 发布时会被拦的（只看启用行）：没传图。碎片图 2026-10-23 起不花碎片，不再要求碎片价 */
export function stageImagePublishIssues(x: StageImage): MsgKey[] {
  if (!x.enabled) return [];
  const out: MsgKey[] = [];
  if (!x.thumbImg || !x.img) out.push("album.iss.noImg");
  return out;
}

/** 换类型：碎片价一律清 0（碎片图 2026-10-23 起不花碎片，同免费图） */
export function withImageType(x: StageImage, imageType: string): StageImage {
  return { ...x, imageType, priceFrag: 0 };
}

/** 这一关一共几张（含第 1 张、含停用） */
export const stageImageCount = (images: StageImage[], s: Stage) => images.filter((x) => belongsTo(x, s)).length;

/**
 * 保存前把图片的所属关换成真 ID：写着 localKey 的，按「关卡列表里 localKey 对得上、已有 st_ 号」的那一关换掉。
 * 换不出来（那一关还没建成）返回 null——调用方跳过这张，下一轮再建
 */
export function resolveStageId(x: StageImage, stages: Stage[]): string | null {
  const s = stages.find((st) => st.localKey === x.stageId || (st.stageId != null && st.stageId === x.stageId));
  return s?.stageId ?? null;
}

/** 拖动后重排第 2 张起：按给定顺序排成 2、3、4…，只返回 slot 变了的行 */
export function resequence(ordered: StageImage[]): StageImage[] {
  return ordered.flatMap((x, i) => (x.slot === 2 + i ? [] : [{ ...x, slot: 2 + i }]));
}

/** **一关**的图里启用行 slot 撞了的（localKey）。只提示；调用方先按所属关筛好（同一关的图 stageId 可能新旧两种写法，见 belongsTo） */
export function duplicateSlots(images: StageImage[]): Set<string> {
  const g = new Map<string, string[]>();
  for (const x of images) {
    if (!x.enabled) continue;
    const k = String(x.slot);
    g.set(k, [...(g.get(k) ?? []), x.localKey]);
  }
  return new Set([...g.values()].filter((v) => v.length > 1).flat());
}
